//----------------CORE-----------------//
import React, { useState, Fragment } from "react";
import { useSelector } from "react-redux";
import hexRgb from "hex-rgb";

//--------------MUI COMPS----------------//
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import ListItemButton from "@mui/material/ListItemButton";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import Tooltip from "@mui/material/Tooltip";
import Fab from "@mui/material/Fab";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Zoom from "@mui/material/Zoom";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dragable from "components/Dragable";

//---------------EXTERNAL---------------//
import { splitTagChips } from "utils/solutionTags";

// D30 (3) (roles prod promotion, Stage 3, 2026-09-25): ONE tag filter for the main solutions
// page and Solution Management. It is controlled: the page owns the selection (tag ids) and
// the match mode ("any", the default, or "all"), and passes the tags its solutions use, with
// how many solutions carry each (utils/solutionTags tagsInUse) - unused tags are not listed.
export default function TagFilter({ tags, selected, mode, onApply, name }) {
  const [openPopup, setOpenPopup] = useState(false);
  const [tempSelected, setTempSelected] = useState([]);
  const [tempMode, setTempMode] = useState("any");
  const [query, setQuery] = useState("");
  const metaDataValue = useSelector((state) => state.metaData);
  const active = selected || [];

  const openFilter = () => {
    setTempSelected(active);
    setTempMode(mode || "any");
    setQuery("");
    setOpenPopup(true);
  };

  const toggleTag = (id) =>
    setTempSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );

  const apply = () => {
    onApply({ selected: tempSelected, mode: tempMode });
    setOpenPopup(false);
  };

  const q = query.trim().toLowerCase();
  const listed = (tags || []).filter(
    (t) => !q || t.name.toLowerCase().includes(q)
  );

  return (
    <div>
      <Dragable bottom={"100px"} right={"30px"} name={name}>
        <Tooltip
          title="Filter by tags"
          TransitionComponent={Zoom}
          placement="top"
          arrow
        >
          <Fab
            style={{ boxShadow: "none" }}
            color="secondary"
            aria-label="filter by tags"
            onClick={openFilter}
          >
            {active.length ? (
              <Badge color="primary" variant="dot">
                <FilterAltIcon />
              </Badge>
            ) : (
              <FilterAltIcon />
            )}
          </Fab>
        </Tooltip>
      </Dragable>
      <Dialog
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        PaperProps={{ style: { width: "500px" } }}
      >
        <DialogTitle>Filter by tags</DialogTitle>
        <DialogContent
          style={{ height: "400px", display: "flex", flexDirection: "column" }}
        >
          <TextField
            size="small"
            margin="dense"
            placeholder="Search tags"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                color="secondary"
                checked={tempMode === "all"}
                onChange={(e) => setTempMode(e.target.checked ? "all" : "any")}
              />
            }
            label={
              tempMode === "all"
                ? "Show solutions with ALL the selected tags"
                : "Show solutions with ANY of the selected tags"
            }
          />
          <List component="nav" style={{ overflowY: "auto", flex: 1 }}>
            <Divider />
            {listed.map((t) => {
              const on = tempSelected.includes(t.id);
              return (
                <Fragment key={t.id}>
                  <ListItemButton
                    onClick={() => toggleTag(t.id)}
                    style={{
                      backgroundColor: on
                        ? metaDataValue?.branding?.secondaryColor
                        : "white",
                      margin: "5px",
                    }}
                  >
                    <ListItemIcon>
                      <LocalOfferIcon style={{ color: on ? "white" : "" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t.name}
                      style={{ color: on ? "white" : "" }}
                    />
                    <span style={{ color: on ? "white" : "grey", fontSize: 13 }}>
                      {t.count === 1 ? "1 solution" : `${t.count} solutions`}
                    </span>
                  </ListItemButton>
                  <Divider />
                </Fragment>
              );
            })}
            {!listed.length ? (
              <p style={{ color: "#c8c8c8", textAlign: "center", marginTop: 20 }}>
                {(tags || []).length
                  ? "No tags match your search"
                  : "These solutions have no tags"}
              </p>
            ) : null}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTempSelected([])}
            disabled={!tempSelected.length}
          >
            Clear
          </Button>
          <Button
            onClick={() => setOpenPopup(false)}
            style={{ color: "#bf3535" }}
          >
            Cancel
          </Button>
          <Button onClick={apply} color="primary">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// The page's active tag filter above its cards: one removable chip per selected tag.
// `tags` are the selected tags as [{ id, name }].
export function ActiveTagFilter({ tags, mode, onRemove }) {
  if (!tags || !tags.length) return null;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 20,
      }}
    >
      <span style={{ color: "grey", fontSize: 13 }}>
        {tags.length > 1
          ? mode === "all"
            ? "Solutions with all of:"
            : "Solutions with any of:"
          : "Solutions with:"}
      </span>
      {tags.map((t) => (
        <Chip
          key={t.id}
          color="secondary"
          label={t.name}
          variant="filled"
          onDelete={() => onRemove(t.id)}
        />
      ))}
    </div>
  );
}

// A card's tag chips: at most 10, the rest summed up as "+N more" (their names in its
// tooltip). Clicking a chip filters the page by that tag; the click does not reach the card.
export function SolutionTagChips({ tags, onTagClick, justify = "flex-end" }) {
  const metaDataValue = useSelector((state) => state.metaData);
  if (!tags || !tags.length) return null;
  const secondary = metaDataValue?.branding?.secondaryColor;
  const rgb = secondary ? hexRgb(secondary) : { red: 96, green: 125, blue: 139 };
  const chipStyle = {
    color: secondary,
    backgroundColor: `rgb(${rgb.red}, ${rgb.green}, ${rgb.blue},0.1)`,
    fontWeight: "400",
    fontSize: "11px",
    borderRadius: 4,
  };
  const keepOffCard = (e) => e.stopPropagation();
  const { shown, hidden } = splitTagChips(tags);
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        justifyContent: justify,
      }}
    >
      {shown.map((t) => (
        <Chip
          key={t.id}
          label={t.name}
          size="small"
          style={chipStyle}
          onMouseDown={keepOffCard}
          onClick={(e) => {
            keepOffCard(e);
            onTagClick(t.id);
          }}
        />
      ))}
      {hidden.length ? (
        <Tooltip title={hidden.map((t) => t.name).join(", ")}>
          <Chip
            label={`+${hidden.length} more`}
            size="small"
            style={chipStyle}
            onMouseDown={keepOffCard}
            onClick={keepOffCard}
          />
        </Tooltip>
      ) : null}
    </div>
  );
}
